import React, { useState } from 'react';
import { KoobConfirmationModal } from './KoobConfirmModal';
import { useTranslation } from 'react-i18next';
import { useApp } from 'hooks';
import { RoleEnum } from '@koob/enums';

export const KoobMutliSelectGrid = ({
  items,
  onDelete,
  renderCard,
  GridComponent,
  onSelection,
  initialSelected,
}) => {
  const { t } = useTranslation('multiSelectGrid');
  const { user } = useApp();

  const initialValues = initialSelected ?? [];
  const [selectedItems, setSelectedItems] = useState(initialValues);
  const [multiSelectMode, setMultiSelectMode] = useState(
    !!initialSelected?.length ?? false,
  );
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const toggleItemSelection = (itemId) => {
    const newSelection = selectedItems.includes(itemId)
      ? selectedItems.filter((id) => id !== itemId)
      : [...selectedItems, itemId];

    setSelectedItems(newSelection);
    if (onSelection) {
      onSelection(itemId);
    }
  };

  const toggleMultiSelectMode = () => {
    const newMode = !multiSelectMode;
    setMultiSelectMode(newMode);
    if (!newMode) {
      setSelectedItems([]);
    }
  };

  const handleCancel = () => {
    setShowConfirmModal(false);
  };

  const handleDelete = () => {
    if (selectedItems.length > 0) {
      onDelete(selectedItems);
      setSelectedItems([]);
      setMultiSelectMode(false);
      setShowConfirmModal(false);
    }
  };

  return (
    <div className="w-full">
      <div className="w-full">
        {RoleEnum.KOOB_ADMIN === user?.role && (
          <div className="w-full flex justify-end mb-6">
            <button
              onClick={toggleMultiSelectMode}
              className={[
                multiSelectMode
                  ? 'bg-gray-800 text-white hover:bg-gray-700'
                  : 'bg-red-500 text-white hover:bg-red-600',
                'px-6 py-2.5 rounded-lg font-medium transition-all border-none',
              ].join(' ')}
              data-testid={`multi-select-${multiSelectMode}`}
            >
              {multiSelectMode
                ? t('disableMultiDelete')
                : t('enableMultiDelete')}
            </button>
          </div>
        )}

        {multiSelectMode && selectedItems.length > 0 && (
          <div className="mb-4 p-4 bg-orange-50 border border-orange-200 rounded-lg flex justify-between items-center">
            <span className="text-orange-900 font-medium">
              {t('numberOfSelection', { count: selectedItems.length })}
            </span>
            <button
              onClick={() => setShowConfirmModal(true)}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 border-none"
              data-testid="delete"
            >
              <i className="fa-regular fa-trash-can" style={{ fontSize: 18 }} />
              {t('misc:delete')}
            </button>
          </div>
        )}

        {GridComponent ? (
          <GridComponent>
            {items.map((item) => (
              <div
                key={item.id}
                onClick={() => multiSelectMode && toggleItemSelection(item.id)}
                className={`h-full rounded-2xl transition-all ${
                  multiSelectMode ? 'cursor-pointer hover:shadow-md' : ''
                } ${
                  selectedItems.includes(item.id) ? 'ring-4 ring-red-500' : ''
                }`}
              >
                {renderCard(item, {
                  isSelected: selectedItems.includes(item.id),
                  isMultiSelectMode: multiSelectMode,
                  onToggle: () =>
                    multiSelectMode && toggleItemSelection(item.id),
                })}
              </div>
            ))}
          </GridComponent>
        ) : (
          <div className="w-full grid gap-4 grid-cols-1 min-[1000px]:grid-cols-2 min-[1200px]:grid-cols-3 min-[1700px]:grid-cols-4">
            {items.map((item) => (
              <div
                key={item.id}
                onClick={() => multiSelectMode && toggleItemSelection(item.id)}
                className={`h-full bg-white rounded-lg transition-all ${
                  multiSelectMode ? 'cursor-pointer hover:shadow-md' : ''
                } ${
                  selectedItems.includes(item.id) ? 'ring-4 ring-blue-500' : ''
                }`}
              >
                {renderCard(item, {
                  isSelected: selectedItems.includes(item.id),
                  isMultiSelectMode: multiSelectMode,
                  onToggle: () =>
                    multiSelectMode && toggleItemSelection(item.id),
                })}
              </div>
            ))}
          </div>
        )}
      </div>

      <KoobConfirmationModal
        handleCancel={handleCancel}
        modalIsShown={showConfirmModal}
        translation={t}
        description={t('misc:confirmationModalDescription', {
          action: 'delete',
        })}
        handleSubmit={handleDelete}
      />
    </div>
  );
};
